import { test } from 'last-validator'

const PHONE_SEPARATOR_PATTERN = /[\s\-()./]/g

/**
 * Simple phone number validation for Slovak and Czech phone numbers
 * Supports both international (+421..., +420...) and national formats (0904...)
 */
export function validatePhoneNumber(value: string | number): boolean {
    // Allow empty values - phone is optional
    if (!value && value !== 0) {
        return true
    }

    // Coerce to string (Firestore may store phone as a number)
    const str = String(value)
    if (str.trim() === '') {
        return true
    }

    const trimmed = str.trim()

    // Basic hygiene checks for '+'
    const plusCount = (trimmed.match(/\+/g) || []).length
    if (plusCount > 1) {
        return false
    }
    if (plusCount === 1) {
        // '+' must be the very first character and not followed by a space
        if (!trimmed.startsWith('+')) return false
        if (trimmed.length > 1 && trimmed[1] === ' ') return false
    }

    // Remove common human formatting separators for validation.
    const cleaned = trimmed.replace(PHONE_SEPARATOR_PATTERN, '')

    // Check if it only contains valid characters (digits and optional leading +)
    if (!/^\+?\d+$/.test(cleaned)) {
        return false
    }

    // International format validation
    if (cleaned.startsWith('+')) {
        // Remove the '+' to get just the digits
        const digits = cleaned.slice(1)

        // Must have between 7-15 digits after the '+'
        if (digits.length < 7 || digits.length > 15) {
            return false
        }

        // Validate specific country formats we know
        // Slovak: +421 followed by 9 digits
        if (cleaned.startsWith('+421')) {
            return /^\+421[1-9]\d{8}$/.test(cleaned)
        }

        // Czech: +420 followed by 9 digits
        if (cleaned.startsWith('+420')) {
            return /^\+420[1-9]\d{8}$/.test(cleaned)
        }

        // US/Canada: +1 followed by 10 digits
        if (cleaned.startsWith('+1')) {
            return /^\+1[2-9]\d{9}$/.test(cleaned)
        }

        // Germany: +49 followed by 10-11 digits
        if (cleaned.startsWith('+49')) {
            return /^\+49[1-9]\d{9,10}$/.test(cleaned)
        }

        // UK: +44 followed by 10 digits
        if (cleaned.startsWith('+44')) {
            return /^\+44[1-9]\d{9}$/.test(cleaned)
        }

        // Austria: +43 followed by 10-13 digits
        if (cleaned.startsWith('+43')) {
            return /^\+43[1-9]\d{9,12}$/.test(cleaned)
        }

        // France: +33 followed by 9 digits
        if (cleaned.startsWith('+33')) {
            return /^\+33[1-9]\d{8}$/.test(cleaned)
        }

        // Netherlands: +31 followed by 9 digits
        if (cleaned.startsWith('+31')) {
            return /^\+31[1-9]\d{8}$/.test(cleaned)
        }

        // Belgium: +32 followed by 8-9 digits
        if (cleaned.startsWith('+32')) {
            return /^\+32[1-9]\d{7,8}$/.test(cleaned)
        }

        // Switzerland: +41 followed by 9 digits
        if (cleaned.startsWith('+41')) {
            return /^\+41[1-9]\d{8}$/.test(cleaned)
        }

        // Poland: +48 followed by 9 digits
        if (cleaned.startsWith('+48')) {
            return /^\+48[1-9]\d{8}$/.test(cleaned)
        }

        // Italy: +39 followed by 9-10 digits
        if (cleaned.startsWith('+39')) {
            return /^\+39[1-9]\d{8,9}$/.test(cleaned)
        }

        // Spain: +34 followed by 9 digits
        if (cleaned.startsWith('+34')) {
            return /^\+34[6-9]\d{8}$/.test(cleaned)
        }

        // For any other country code, accept if it has reasonable length (7-15 digits)
        // This covers countries we haven't explicitly listed
        return true
    }

    // National format validation (Slovak/Czech)
    // Slovak/Czech mobile: 09XX XXX XXX (10 digits starting with 0)
    if (/^0[1-9]\d{8}$/.test(cleaned)) {
        return true
    }

    // Allow 9-digit national format without leading 0 (uncommon but valid)
    if (/^[1-9]\d{8}$/.test(cleaned)) {
        return true
    }

    return false
}

/**
 * Normalize Slovak/Czech phone numbers to international E.164 format
 * Converts national format (0904...) to international (+421904...)
 */
export function normalizeSlovakPhoneNumber(value: string | number, defaultCountryCode: string = '+421'): string {
    if (!value && value !== 0) return String(value ?? '')

    // Coerce to string (Firestore may store phone as a number)
    const str = String(value)
    const trimmed = str.trim()

    // Already in international format
    if (trimmed.startsWith('+')) {
        return trimmed.replace(PHONE_SEPARATOR_PATTERN, '')
    }

    // Remove common human formatting separators
    const cleaned = trimmed.replace(PHONE_SEPARATOR_PATTERN, '')

    // National format starting with 0 -> convert to international
    if (/^0[1-9]\d{8}$/.test(cleaned)) {
        return `${defaultCountryCode}${cleaned.slice(1)}`
    }

    // 9-digit format without leading 0 -> add country code
    if (/^[1-9]\d{8}$/.test(cleaned)) {
        return `${defaultCountryCode}${cleaned}`
    }

    // Return as-is if we can't normalize it
    return trimmed
}

export const isPhoneNumber = test(validatePhoneNumber, 'Neplatné telefónne číslo.')
